import { useState } from "react";
import { Gift, Tag, Key, Wrench, Lock, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TYPE_META = {
  reward: { icon: Gift, label: "Reward" },
  discount: { icon: Tag, label: "Discount" },
  access: { icon: Key, label: "Access" },
  service: { icon: Wrench, label: "Service" },
};

const STATUS_BADGE = {
  available: {
    text: "Available",
    cls: "bg-blue-50 text-[#0055FF] border border-blue-200",
    dot: "bg-[#0055FF]",
  },
  redeemed: {
    text: "Redeemed",
    cls: "bg-gray-100 text-gray-600 border border-gray-200",
    dot: "bg-gray-400",
  },
  locked: {
    text: "Locked",
    cls: "bg-amber-50 text-amber-700 border border-amber-200",
    dot: "bg-amber-500",
  },
};

const UtilityCard = ({ utility, ownershipVerified, receiptId, onUpdate }) => {
  const meta = TYPE_META[utility.type] || TYPE_META.reward;
  const Icon = meta.icon;
  // When ownership is not verified, force visual lock state
  const effectiveStatus = ownershipVerified ? utility.status : "locked";
  const badge = STATUS_BADGE[effectiveStatus];
  const [redeeming, setRedeeming] = useState(false);

  const canRedeem =
    ownershipVerified && utility.status === "available" && !redeeming;

  const handleRedeem = async () => {
    setRedeeming(true);
    try {
      const { data } = await api.post(
        `/receipts/${receiptId}/utilities/${utility.id}/redeem`
      );
      toast.success("Benefit redeemed successfully");
      onUpdate?.(data);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not redeem this benefit"
      );
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div
      data-testid={`utility-${utility.id}`}
      className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-3"
    >
      <div className="flex items-start gap-3">
        <div
          className={`shrink-0 h-10 w-10 rounded-lg border flex items-center justify-center ${
            effectiveStatus === "available"
              ? "bg-blue-50 border-blue-200 text-[#0055FF]"
              : effectiveStatus === "redeemed"
              ? "bg-gray-50 border-gray-200 text-gray-500"
              : "bg-gray-50 border-gray-200 text-amber-600"
          }`}
        >
          {effectiveStatus === "locked" ? (
            <Lock size={16} strokeWidth={2.2} />
          ) : effectiveStatus === "redeemed" ? (
            <Check size={16} strokeWidth={2.4} />
          ) : (
            <Icon size={16} strokeWidth={2.2} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500">
            {meta.label}
          </div>
          <div className="font-display font-semibold text-gray-900 text-[15px] mt-0.5 leading-snug">
            {utility.title}
          </div>
          <div className="text-xs text-gray-500 mt-1 leading-relaxed">
            {utility.description}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mt-1">
        <span
          className={`inline-flex items-center gap-1.5 ${badge.cls} px-2.5 py-0.5 rounded-full text-[11px] font-semibold`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${badge.dot}`} />
          {badge.text}
        </span>
        {utility.code && effectiveStatus !== "locked" && (
          <span
            data-testid={`utility-code-${utility.id}`}
            className="font-mono text-[11px] bg-gray-100 text-gray-700 border border-gray-200 px-2 py-0.5 rounded-md"
          >
            {utility.code}
          </span>
        )}
      </div>

      {ownershipVerified && utility.status === "available" && (
        <button
          onClick={handleRedeem}
          disabled={!canRedeem}
          data-testid={`redeem-${utility.id}`}
          className="mt-1 w-full inline-flex items-center justify-center gap-1.5 bg-[#0055FF] text-white text-xs font-semibold px-3 py-2 rounded-lg shadow-sm hover:bg-[#0044CC] hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-wait"
        >
          <Sparkles size={13} />
          {redeeming ? "Redeeming…" : "Redeem"}
        </button>
      )}
      {ownershipVerified && utility.status === "redeemed" && (
        <div className="mt-1 text-[11px] text-gray-500 text-center">
          Redeemed by verified owner
        </div>
      )}
    </div>
  );
};

export default function BenefitsSection({ status, receipt, onReceiptUpdate }) {
  // Hide entirely for mismatch / not_found
  if (status === "mismatch" || status === "not_found") return null;

  const utilities = receipt?.utilities || [];
  if (utilities.length === 0) return null;

  const ownershipVerified = status === "verified";

  const handleUtilityUpdate = (updated) => {
    if (!receipt || !onReceiptUpdate) return;
    const next = {
      ...receipt,
      utilities: utilities.map((u) => (u.id === updated.id ? updated : u)),
    };
    onReceiptUpdate(next);
  };

  return (
    <>
      <div className="px-8 sm:px-12">
        <div className="flex items-center gap-4">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-[10px] font-semibold tracking-[0.3em] uppercase text-gray-400">
            Benefits
          </span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>
      </div>

      <div
        data-testid="benefits-section"
        className="px-8 sm:px-12 py-8"
      >
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="font-display font-bold text-gray-900 text-xl">
              Benefits &amp; Access
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {ownershipVerified
                ? "Available to verified owner."
                : "Benefits unlock once ownership is verified."}
            </p>
          </div>
          <span className="text-xs font-mono text-gray-400">
            {utilities.length} benefit{utilities.length === 1 ? "" : "s"}
          </span>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {utilities.map((u) => (
            <UtilityCard
              key={u.id}
              utility={u}
              ownershipVerified={ownershipVerified}
              receiptId={receipt.id}
              onUpdate={handleUtilityUpdate}
            />
          ))}
        </div>
      </div>
    </>
  );
}
